import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { HealthDataEntry } from './useHealthKit';

export interface WeightEntry {
  id: string;
  weight: number;
  logged_at: string;
  notes?: string;
  source?: 'manual' | 'health';
}

export function useWeightHistory(userId: string | undefined) {
  const [weightHistory, setWeightHistory] = useState<WeightEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchWeightHistory = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('weight_history')
        .select('*')
        .eq('user_id', userId)
        .order('logged_at', { ascending: true })
        .limit(90); // Last 90 days max

      if (error) throw error;

      setWeightHistory(data?.map(entry => ({
        id: entry.id,
        weight: Number(entry.weight),
        logged_at: entry.logged_at,
        notes: entry.notes || undefined,
        source: 'manual' as const,
      })) || []);
    } catch (error) {
      console.error('Error fetching weight history:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchWeightHistory();
  }, [fetchWeightHistory]);

  const logWeight = useCallback(async (weight: number, notes?: string) => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('weight_history')
        .insert({
          user_id: userId,
          weight,
          notes,
          logged_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      const newEntry: WeightEntry = {
        id: data.id,
        weight: Number(data.weight),
        logged_at: data.logged_at,
        notes: data.notes || undefined,
        source: 'manual',
      };

      setWeightHistory(prev => [...prev, newEntry]);
      return newEntry;
    } catch (error) {
      console.error('Error logging weight:', error);
      return null;
    }
  }, [userId]);

  const deleteWeightEntry = useCallback(async (id: string) => {
    if (!userId) return false;

    try {
      const { error } = await supabase
        .from('weight_history')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setWeightHistory(prev => prev.filter(entry => entry.id !== id));
      return true;
    } catch (error) {
      console.error('Error deleting weight entry:', error);
      return false;
    }
  }, [userId]);

  // Import weight data from HealthKit/Health Connect
  const importFromHealth = useCallback(async (
    healthWeightData: HealthDataEntry[],
    weightUnit: 'lbs' | 'kg' = 'lbs'
  ): Promise<number> => {
    if (!userId || !healthWeightData?.length) return 0;

    let imported = 0;
    const existingDates = new Set(
      weightHistory.map(entry => entry.logged_at.split('T')[0])
    );

    for (const entry of healthWeightData) {
      const entryDate = entry.date.split('T')[0];
      
      // Skip if we already have data for this date
      if (existingDates.has(entryDate)) continue;

      // Convert kg to lbs if needed (HealthKit returns kg for body mass)
      const weightValue = weightUnit === 'lbs' 
        ? Math.round(entry.value * 2.20462 * 10) / 10 // kg to lbs
        : Math.round(entry.value * 10) / 10;

      try {
        const { data, error } = await supabase
          .from('weight_history')
          .insert({
            user_id: userId,
            weight: weightValue,
            notes: `Imported from ${entry.unit === 'kg' ? 'Health' : 'Health'}`,
            logged_at: entry.date,
          })
          .select()
          .single();

        if (!error && data) {
          const newEntry: WeightEntry = {
            id: data.id,
            weight: Number(data.weight),
            logged_at: data.logged_at,
            notes: data.notes || undefined,
            source: 'health',
          };
          setWeightHistory(prev => [...prev, newEntry].sort((a, b) => 
            new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime()
          ));
          imported++;
        }
      } catch (error) {
        console.error('Error importing weight entry:', error);
      }
    }

    return imported;
  }, [userId, weightHistory]);

  return {
    weightHistory,
    loading,
    logWeight,
    deleteWeightEntry,
    importFromHealth,
    refetch: fetchWeightHistory,
  };
}